import { useParams, Link } from 'react-router-dom'
import {ArrowLeft, Globe, Send, Github, CheckCircle2, XCircle, ExternalLink, Calendar, ShieldCheck, ShieldOff, Clock, Copy, Check, ListChecks, Loader2, FileDown} from 'lucide-react'

function XLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  )
}
import { useState, useCallback } from 'react'
import { exportAuditPdf } from '@/lib/exportAuditPdf'
import { useShareAudit } from '@/components/AuditShareCard'
import Layout from '@/components/Layout'
import ScoreRing from '@/components/ScoreRing'
import AuditBadge from '@/components/AuditBadge'
import { CHAIN_LABELS, AUDIT_CHECKLIST, CHECK_KEYWORDS, toProjectSlug } from '@/lib/types'
import type { VulnSeverity, Vulnerability, AuditResult, ChecklistOverride } from '@/lib/types'
import { useAllProjects } from '@/hooks/useProjects'

// ─── palette ──────────────────────────────────────────────────────────────────
const C = {
  bg:         '#000000',
  turq:       '#1ad6c4',
  turqDim:    'rgba(26,214,196,0.10)',
  borderTurq: 'rgba(26,214,196,0.28)',
  border:     'rgba(255,255,255,0.07)',
  textSecond: '#b4c0cc',
  textMuted:  '#5a6470',
}

// ─── severity map ─────────────────────────────────────────────────────────────
const SEV: Record<VulnSeverity, { label: string; color: string; bar: string }> = {
  critical: { label: 'Critical', color: '#f87171', bar: '#ef4444' },
  high:     { label: 'High',     color: '#fb923c', bar: '#f97316' },
  medium:   { label: 'Medium',   color: '#facc15', bar: '#eab308' },
  low:      { label: 'Low',      color: '#60a5fa', bar: '#3b82f6' },
  info:     { label: 'Info',     color: '#94a3b8', bar: '#64748b' },
}

// ─── checklist helper ─────────────────────────────────────────────────────────
// Uses manual admin overrides when present; falls back to keyword-derive
function buildChecklist(audit: AuditResult) {
  const overrides = audit.checklistOverrides ?? {}
  const hasOverrides = Object.values(overrides).some(o => o.state !== null)

  if (hasOverrides) {
    // Use manual overrides set in admin panel
    return AUDIT_CHECKLIST.map((item) => {
      const o: ChecklistOverride = overrides[item.id] ?? { state: null }
      return {
        ...item,
        passed: o.state === 'pass',
        notSet: o.state === null,
        reason: o.state === 'fail' ? (o.reason ?? '') : '',
      }
    })
  }

  // Fallback: derive from unresolved vulnerability keywords
  const unresolvedText = audit.vulnerabilities
    .filter((v) => !v.resolved)
    .map((v) => `${v.title} ${v.description}`.toLowerCase())

  return AUDIT_CHECKLIST.map((item) => {
    const keywords = CHECK_KEYWORDS[item.id] ?? []
    const failed = unresolvedText.some((text) =>
      keywords.some((kw) => text.includes(kw)),
    )
    return { ...item, passed: !failed, notSet: false, reason: '' }
  })
}

// ─── sub-components ───────────────────────────────────────────────────────────
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1800)
      }}
      className="p-1 rounded transition-colors hover:bg-white/5"
      style={{ color: copied ? C.turq : C.textMuted }}
      title="Copy address"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  )
}

function SeverityPill({ sev }: { sev: VulnSeverity }) {
  const m = SEV[sev]
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{ background: `${m.bar}18`, color: m.color }}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: m.bar }} />
      {m.label}
    </span>
  )
}

// Vulnerability row — left accent bar, no background fill
function VulnRow({ v }: { v: Vulnerability }) {
  const color = v.resolved ? '#34d399' : SEV[v.severity].bar
  return (
    <div
      className="pl-4 py-3"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className="text-sm font-medium text-white leading-snug">{v.title}</span>
        <SeverityPill sev={v.severity} />
        {v.resolved && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(52,211,153,0.10)', color: '#34d399' }}
          >
            <CheckCircle2 style={{ width: 10, height: 10 }} /> Resolved
          </span>
        )}
      </div>
      <p className="text-sm leading-relaxed" style={{ color: C.textMuted }}>{v.description}</p>
    </div>
  )
}

// Checklist row — bare, left-edge colored dot, no box
function ChecklistRow({ item }: { item: { id: string; label: string; description: string; passed: boolean; notSet?: boolean; reason?: string } }) {
  const color = item.notSet ? C.textMuted : item.passed ? '#34d399' : '#f87171'
  return (
    <div className="flex flex-col py-2.5" style={{ borderBottom: `1px solid ${C.border}` }}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {item.notSet
            ? <div style={{ width: 16, height: 16, borderRadius: 3, border: `1px solid ${C.textMuted}`, opacity: 0.4 }} />
            : item.passed
              ? <CheckCircle2 style={{ width: 16, height: 16, color: '#34d399' }} />
              : <XCircle style={{ width: 16, height: 16, color: '#f87171' }} />
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: item.notSet ? C.textMuted : '#ffffff' }}>{item.label}</span>
            {!item.notSet && (
              <span
                className="text-[10px] font-bold uppercase tracking-wider"
                style={{ color }}
              >
                {item.passed ? 'Pass' : 'Fail'}
              </span>
            )}
          </div>
          <p className="text-xs mt-0.5 leading-snug" style={{ color: C.textMuted }}>{item.description}</p>
        </div>
      </div>
      {/* Fail reason */}
      {!item.passed && !item.notSet && item.reason && (
        <div
          className="mt-2 ml-7 text-xs leading-snug px-2.5 py-2 rounded-md"
          style={{ background: 'rgba(248,113,113,0.07)', color: '#f87171', borderLeft: '2px solid rgba(248,113,113,0.4)' }}
        >
          {item.reason}
        </div>
      )}
    </div>
  )
}

// ─── section label ────────────────────────────────────────────────────────────
function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      className="flex items-center justify-between py-2.5"
      style={{ borderBottom: `1px solid ${C.border}` }}
    >
      <span className="text-xs" style={{ color: C.textMuted }}>{label}</span>
      <span className="text-xs font-medium text-white text-right">{children}</span>
    </div>
  )
}

// ─── section label ────────────────────────────────────────────────────────────
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-[11px] font-bold uppercase tracking-[0.12em] mb-4 flex items-center gap-2"
      style={{ color: C.turq }}
    >
      {children}
    </p>
  )
}

// ─── main component ───────────────────────────────────────────────────────────
export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>()
  const { projects, loading } = useAllProjects()
  // Match by slug (project name) first, fall back to _id for backwards-compat
  const project = projects.find((p) => toProjectSlug(p.name) === id) ?? projects.find((p) => p._id === id)

  // ── ALL hooks must be called before any conditional return ──
  const [pdfLoading, setPdfLoading] = useState(false)
  const handleExportPdf = useCallback(async () => {
    if (!project) return
    setPdfLoading(true)
    try {
      await exportAuditPdf(project)
    } finally {
      setPdfLoading(false)
    }
  }, [project])

  // Share on X — always call hook (project may be undefined, guard inside hook)
  const shareProject = project ?? { audit: null } as unknown as typeof project
  const { share: handleShare, loading: shareLoading, CardPortal } = useShareAudit(shareProject!)

  if (loading) {
    return (
      <Layout>
        <section className="min-h-[70vh] flex items-center justify-center" style={{ background: C.bg }}>
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: C.turq }} />
        </section>
      </Layout>
    )
  }

  if (!project) {
    return (
      <Layout>
        <section className="min-h-[70vh] flex items-center justify-center" style={{ background: C.bg }}>
          <div className="text-center px-5">
            <ShieldOff className="w-12 h-12 mx-auto mb-4" style={{ color: C.turq }} />
            <h1 className="text-2xl font-semibold text-white mb-2">Project Not Found</h1>
            <p className="text-sm mb-6" style={{ color: C.textMuted }}>
              This project doesn't exist or the link may be outdated.
            </p>
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:brightness-110"
              style={{ background: C.turq, color: '#000' }}
            >
              <ArrowLeft className="w-4 h-4" /> Browse Projects
            </Link>
          </div>
        </section>
      </Layout>
    )
  }

  const { audit, social } = project
  const isCompleted   = audit?.status === 'completed'
  const passed        = audit?.verdict === 'passed'
  const critCount     = audit?.vulnerabilities.filter((v) => v.severity === 'critical' && !v.resolved).length ?? 0
  const highCount     = audit?.vulnerabilities.filter((v) => v.severity === 'high'     && !v.resolved).length ?? 0
  const resolvedCount = audit?.vulnerabilities.filter((v) => v.resolved).length ?? 0
  const checklist     = isCompleted && audit ? buildChecklist(audit) : []
  const passedChecks  = checklist.filter((i) => i.passed).length

  const shortAddr = project.contractAddress.slice(0, 8) + '…' + project.contractAddress.slice(-6)

  return (
    <Layout>
      <div style={{ background: C.bg, minHeight: '100vh' }}>

        {/* ── Hero banner ── */}
        <div
          className="w-full relative"
          style={{
            height: '260px',
            background: project.bannerUrl
              ? `url(${project.bannerUrl}) center/cover no-repeat`
              : `linear-gradient(135deg, #040810 0%, #061214 60%, #020808 100%)`,
          }}
        >
          {/* grid overlay */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(${C.turq}18 1px, transparent 1px), linear-gradient(90deg, ${C.turq}18 1px, transparent 1px)`,
              backgroundSize: '48px 48px',
            }}
          />
          {/* bottom fade to black */}
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to top, #000 0%, transparent 55%)' }}
          />
          {/* Back link */}
          <div className="absolute top-5 left-5">
            <Link
              to="/projects"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors hover:text-white"
              style={{ color: C.textSecond, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)', border: `1px solid ${C.border}` }}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Projects
            </Link>
          </div>
        </div>

        {/* ── Identity strip + social toolbar ── */}
        <div className="max-w-[900px] mx-auto px-5 sm:px-8">

          {/* Avatar + name row */}
          <div className="flex items-end gap-5 -mt-14 mb-5 relative z-10">
            {/* Logo avatar */}
            <div
              className="w-[88px] h-[88px] sm:w-[100px] sm:h-[100px] rounded-2xl flex-shrink-0 flex items-center justify-center text-2xl font-bold border-[3px]"
              style={{
                background:  project.logoUrl ? `url(${project.logoUrl}) center/cover` : '#080c10',
                borderColor: '#000',
                color:        C.turq,
                boxShadow:   `0 0 0 1px ${C.borderTurq}, 0 8px 32px rgba(0,0,0,0.7)`,
              }}
            >
              {!project.logoUrl && project.symbol.slice(0, 2)}
            </div>

            {/* Name block */}
            <div className="pb-1 flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2.5 mb-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight tracking-tight">
                  {project.name}
                </h1>
                <span
                  className="text-xs px-2.5 py-0.5 rounded-full font-mono font-semibold"
                  style={{ background: C.turqDim, color: C.turq, border: `1px solid ${C.borderTurq}` }}
                >
                  {project.symbol}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs" style={{ color: C.textMuted }}>{CHAIN_LABELS[project.chain]}</span>
                <span className="text-xs" style={{ color: C.border }}>·</span>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-mono" style={{ color: C.textMuted }}>{shortAddr}</span>
                  <CopyButton text={project.contractAddress} />
                </div>
              </div>
            </div>
          </div>

          {/* ── Social link toolbar ── */}
          {social && Object.values(social).some(Boolean) && (
            <div
              className="flex items-center gap-1 flex-wrap mb-6"
              style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: '16px' }}
            >
              {social.website && (
                <a
                  href={social.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:text-white"
                  style={{ color: C.textMuted, background: 'rgba(255,255,255,0.04)' }}
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span>Website</span>
                </a>
              )}
              {social.twitter && (
                <a
                  href={social.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:text-white"
                  style={{ color: C.textMuted, background: 'rgba(255,255,255,0.04)' }}
                >
                  <XLogo className="w-3.5 h-3.5" />
                </a>
              )}
              {social.telegram && (
                <a
                  href={social.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:text-white"
                  style={{ color: C.textMuted, background: 'rgba(255,255,255,0.04)' }}
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Telegram</span>
                </a>
              )}
              {social.discord && (
                <a
                  href={social.discord}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:text-white"
                  style={{ color: C.textMuted, background: 'rgba(255,255,255,0.04)' }}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Discord</span>
                </a>
              )}
              {social.github && (
                <a
                  href={social.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:text-white"
                  style={{ color: C.textMuted, background: 'rgba(255,255,255,0.04)' }}
                >
                  <Github className="w-3.5 h-3.5" />
                  <span>GitHub</span>
                </a>
              )}
            </div>
          )}

          {/* ── Bio / About ── */}
          {(project.bio || project.description) && (
            <div className="mb-10">
              <SectionLabel>About</SectionLabel>
              <p className="text-sm leading-relaxed" style={{ color: C.textSecond }}>
                {project.bio || project.description}
              </p>
            </div>
          )}

          {/* ── Main layout ── */}
          <div className="grid md:grid-cols-[1fr_200px] gap-12 pb-24">

            {/* ── LEFT COLUMN ── */}
            <div className="flex flex-col gap-12">

              {/* ── VERDICT — full-width, no container ── */}
              {isCompleted && (
                <div>
                  <div className="flex items-center gap-4 mb-3">
                    {passed
                      ? <CheckCircle2 className="w-9 h-9 flex-shrink-0" style={{ color: '#34d399' }} />
                      : <XCircle     className="w-9 h-9 flex-shrink-0" style={{ color: '#f87171' }} />
                    }
                    <div>
                      <div
                        className="text-2xl font-bold tracking-tight"
                        style={{ color: passed ? '#34d399' : '#f87171' }}
                      >
                        Audit {passed ? 'Passed' : 'Failed'}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: C.textMuted }}>
                        Crypto Audit Service — verified result
                      </div>
                    </div>
                    <div className="ml-auto flex-shrink-0 flex items-center gap-3">
                      {audit?.score != null && (
                        <ScoreRing score={audit.score} size={80} />
                      )}
                      <button
                        onClick={handleExportPdf}
                        disabled={pdfLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        style={{ background: C.turqDim, color: C.turq, border: `1px solid ${C.borderTurq}` }}
                        title="Download audit report as PDF"
                      >
                        {pdfLoading
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <FileDown className="w-3.5 h-3.5" />
                        }
                        <span className="hidden sm:inline">{pdfLoading ? 'Generating…' : 'PDF'}</span>
                      </button>
                      <button
                        onClick={handleShare}
                        disabled={shareLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        style={{ background: 'rgba(255,255,255,0.05)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.12)' }}
                        title="Share this audit on X"
                      >
                        {shareLoading
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <XLogo className="w-3.5 h-3.5" />
                        }
                        <span className="hidden sm:inline">{shareLoading ? 'Saving…' : 'Share'}</span>
                      </button>
                    </div>
                  </div>
                  {audit?.summary && (
                    <p className="text-sm leading-relaxed pl-[52px]" style={{ color: C.textMuted }}>
                      {audit.summary}
                    </p>
                  )}
                </div>
              )}

              {/* In-review / pending — no container */}
              {!isCompleted && audit && (
                <div>
                  <div className="flex items-center gap-4 mb-2">
                    <Clock className="w-8 h-8 flex-shrink-0" style={{ color: C.turq }} />
                    <div>
                      <div className="text-xl font-bold text-white">
                        {audit.status === 'in_review' ? 'Audit In Progress' : 'Audit Pending'}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: C.textMuted }}>
                        Crypto Audit Service — review underway
                      </div>
                    </div>
                  </div>
                  {audit.summary && (
                    <p className="text-sm leading-relaxed pl-[52px]" style={{ color: C.textMuted }}>
                      {audit.summary}
                    </p>
                  )}
                </div>
              )}



              {/* ── SECURITY CHECKLIST ── */}
              {isCompleted && checklist.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <SectionLabel>
                      <ListChecks style={{ width: 14, height: 14 }} /> Security Checklist
                    </SectionLabel>
                    <span
                      className="text-xs font-semibold px-2.5 py-1 rounded-full -mt-4"
                      style={{ background: C.turqDim, color: C.turq, border: `1px solid ${C.borderTurq}` }}
                    >
                      {passedChecks}/{checklist.length} passed
                    </span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-10">
                    {checklist.map((item) => (
                      <ChecklistRow key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              )}

              {/* ── VULNERABILITY REPORT ── */}
              {isCompleted && audit && audit.vulnerabilities.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <SectionLabel>Vulnerability Report</SectionLabel>
                    <div className="flex items-center gap-2 flex-wrap -mt-4">
                      {critCount > 0 && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(239,68,68,0.10)', color: '#f87171' }}>
                          {critCount} Critical
                        </span>
                      )}
                      {highCount > 0 && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(249,115,22,0.10)', color: '#fb923c' }}>
                          {highCount} High
                        </span>
                      )}
                      {resolvedCount > 0 && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(52,211,153,0.08)', color: '#34d399' }}>
                          {resolvedCount} Resolved
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-5">
                    {(['critical', 'high', 'medium', 'low', 'info'] as VulnSeverity[]).map((sev) => {
                      const group = audit.vulnerabilities.filter((v) => v.severity === sev)
                      if (!group.length) return null
                      return group.map((v) => <VulnRow key={v.id} v={v} />)
                    })}
                  </div>
                </div>
              )}

              {/* ── NO VULNS ── */}
              {isCompleted && audit && audit.vulnerabilities.length === 0 && (
                <div className="flex items-center gap-4 py-6">
                  <ShieldCheck className="w-10 h-10 flex-shrink-0" style={{ color: '#34d399' }} />
                  <div>
                    <p className="text-base font-semibold text-white">Zero vulnerabilities found</p>
                    <p className="text-sm mt-0.5" style={{ color: C.textMuted }}>
                      This contract passed all security checks with no identified issues.
                    </p>
                  </div>
                </div>
              )}

              {/* ── NO AUDIT YET CTA ── */}
              {!audit && (
                <div>
                  <p className="text-sm mb-4" style={{ color: C.textMuted }}>
                    This contract has not been audited yet. Get a professional security review from Crypto Audit Service.
                  </p>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:brightness-110"
                    style={{ background: C.turq, color: '#000' }}
                  >
                    <ShieldCheck className="w-4 h-4" />
                    Get This Contract Audited
                  </Link>
                </div>
              )}
            </div>

            {/* ── RIGHT SIDEBAR — flat rows, no boxes ── */}
            <div className="flex flex-col gap-8">

              {/* Audit info */}
              <div>
                <SectionLabel>Audit Info</SectionLabel>
                <MetaRow label="Status">
                  <AuditBadge status={audit?.status ?? 'pending'} verdict={audit?.verdict ?? null} size="sm" />
                </MetaRow>
                <MetaRow label="Chain">{CHAIN_LABELS[project.chain]}</MetaRow>
                <MetaRow label="Package">
                  <span className="capitalize">{project.packageTier ?? '—'}</span>
                </MetaRow>
                {audit?.auditedAt && (
                  <MetaRow label="Audited">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(audit.auditedAt).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                      })}
                    </span>
                  </MetaRow>
                )}
              </div>

              {/* PDF export + Share on X */}
              {isCompleted && (
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleExportPdf}
                    disabled={pdfLoading}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: C.turqDim, color: C.turq, border: `1px solid ${C.borderTurq}` }}
                  >
                    {pdfLoading
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <FileDown className="w-3.5 h-3.5" />
                    }
                    {pdfLoading ? 'Generating PDF…' : 'Download PDF Report'}
                  </button>
                  <button
                    onClick={handleShare}
                    disabled={shareLoading}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'rgba(255,255,255,0.05)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.14)' }}
                  >
                    {shareLoading
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <XLogo className="w-3 h-3" />
                    }
                    {shareLoading ? 'Saving image…' : 'Share Audit on X'}
                  </button>
                </div>
              )}

              {/* Contract address */}
              <div>
                <SectionLabel>Contract</SectionLabel>
                <div className="flex items-start gap-1.5">
                  <span className="text-xs font-mono break-all leading-relaxed flex-1" style={{ color: C.textMuted }}>
                    {project.contractAddress}
                  </span>
                  <CopyButton text={project.contractAddress} />
                </div>
              </div>


            </div>

          </div>
        </div>
      </div>
      {/* Off-screen share card — rendered but invisible, captured by html2canvas */}
      <CardPortal />
    </Layout>
  )
}
